const {registerModule, KitModule, Vimeo} = window;

class VideoGallery extends KitModule {
  constructor($element) {
    super($element);

    this.setVars();
    this.bindEvents();
  }

  setVars() {
    this.isDesktop = window.matchMedia('(min-width: 601px)').matches;
    this.isMobile = window.matchMedia('(max-width: 600px)').matches;
    this.$placeholder = this.$element.querySelector('.video--placeholder');
    this.players = [];
    this.isPlaying = false;
    this.parentGallery = this.$element.closest('.gallery');
    this.quotes = this.parentGallery.querySelectorAll('.gallery__quote');
    this.defaultText = this.parentGallery.querySelector('.gallery__body');
    this.currentQuote = false;
  }

  bindEvents() {
    this.fetchAndLoadPoster();
    this.fetchAndLoadVideo();
    this.handleVideoState();
    if (this.isMobile) {
      this.$placeholder.classList.remove('video--placeholder');
    }
    window.addEventListener('resize', this);
  }

  onresize() {
    this.isDesktop = window.matchMedia('(min-width: 601px)').matches;
    this.isMobile = window.matchMedia('(max-width: 600px)').matches;
  }

  fetchAndLoadPoster() {
    this.$video.forEach(($video) => {
      const url = $video.getAttribute('data-url');
      const id = VideoGallery.getEmbedId(url);
      this.fetchThumb($video, id);
    });
  }

  fetchAndLoadVideo() {
    this.$video.forEach(($video) => {
      this.loadVideo($video);
    });
  }

  checkPlaceholderVideo() {
    this.$video.forEach(($video) => {
      if ($video.classList.contains('video--placeholder')) {
        this.$placeholder = $video;
      }
    });
  }

  loadVideo(video) {
    const url = video.getAttribute('data-url');
    const id = VideoGallery.getEmbedId(url);
    const options = {
      id: id,
      playsinline: false,
      // Do Not Track - prevents Vimeo from setting cookies or collecting session data
      dnt: true,
    };

    const player = new Vimeo.Player(video, options);
    this.players.push({video, player});
  }

  handleVideoState() {
    this.$video.forEach(($video) => {
      $video.addEventListener('click', () => this.handleClick($video));
    });
  }

  handleClick(video) {
    if (this.isDesktop && video.matches('.video--placeholder') && !this.isPlaying) {
      this.playVideo(video);
      this.swapQuote(video);
    } else if (this.isDesktop && !video.matches('.video--placeholder')) {
      this.isPlaying = false;
      this.checkPlaceholderVideo();
      VideoGallery.swap(video, this.$placeholder);
      setTimeout(() => {
        this.playVideo(video);
      }, 500);
      this.swapQuote(video);
    } else {
      this.isPlaying = !this.isPlaying;
    }

    if (this.isMobile) {
      this.isPlaying = false;
      this.playVideo(video);
      this.swapQuote(video);
    }
  }

  playVideo(video) {
    this.players.forEach( async (galleryPlayer) => {
      if (galleryPlayer.video === video) {
        const duration = await galleryPlayer.player.getDuration();

        this.isPlaying = true;
        galleryPlayer.player.play();

        this.$element.classList.add('is-playing');
        video.classList.add('is-playing');

        // Vimeo's player seems to drop event registration sporadically. Instead
        // of relying on player.on('ended'), implement gallery reset using a simple
        // timer.
        clearTimeout(this.playbackTimeout);
        this.playbackTimeout = setTimeout(() => {
          if (this.isPlaying) {
            this.isPlaying = false;
            this.$element.classList.remove('is-playing');
            video.classList.remove('is-playing');
          }
        }, (duration + 1) * 1000 );
      } else {
        galleryPlayer.video.classList.remove('is-playing');
        galleryPlayer.player.pause();
      }
    });
  }

  swapQuote(video) {
    const videoIndex = video.getAttribute('data-org-index');
    const quote = [...this.quotes].find((element) => {
      return element.dataset.index == videoIndex;
    });

    if (quote) {
      if (this.currentQuote) {
        this.currentQuote.classList.add('is-hidden');
        this.currentQuote.classList.remove('isVisible');
      } else {
        this.defaultText.classList.add('is-hidden');
        this.defaultText.classList.remove('isVisible');
      }

      quote.classList.remove('is-hidden');

      setTimeout(() => {
        quote.classList.add('isVisible');
      }, 50);

      this.currentQuote = quote;
    } else {
      if (this.currentQuote) {
        this.currentQuote.classList.add('is-hidden');
        this.currentQuote.classList.remove('isVisible');
        this.currentQuote = false;
      }

      this.defaultText.classList.remove('is-hidden');
      setTimeout(() => {
        this.defaultText.classList.add('isVisible');
      }, 50);
    }

    this.defaultText;
  }

  async fetchThumb(video, id) {
    const img = document.createElement('img');

    try {
      const cover = video.getAttribute('data-poster');

      if (cover) {
        img.src = cover;
      } else {
        const videoUrl = encodeURIComponent(`https://vimeo.com/${id}`);
        // size options are 200 , 295 , 640 , 960 , 1280
        const apiUrl = `https://vimeo.com/api/oembed.json?url=${videoUrl}&width=960`;

        const response = await fetch(apiUrl);
        const jsonData = await response.json();
        const cover = jsonData.thumbnail_url;

        img.src = cover;
      }
    } catch (error) {
      const cover = video.getAttribute('data-poster');
      if (cover) {
        img.src = cover;
      } else {
        img.style = 'opacity: 0;';
      }
    } finally {
      video.appendChild(img);
    }
  }

  static swap(thumb, placeholder) {
    const parent = thumb.parentNode || placeholder.parentNode;

    const thumbIndex = thumb.getAttribute('data-index');
    const placeholderIndex = placeholder.getAttribute('data-index');

    // swap placeholder class for style purpose
    placeholder.classList.remove('video--placeholder');
    thumb.classList.add('video--placeholder');
    if (placeholder.classList.contains('is-playing')) {
      placeholder.classList.remove('is-playing');
    }

    // swap indexes
    thumb.setAttribute('data-index', placeholderIndex);
    placeholder.setAttribute('data-index', thumbIndex);

    // switch places
    parent.insertBefore(thumb, parent.children[placeholderIndex]);
    parent.insertBefore(placeholder, parent.children[thumbIndex]);
  }

  static getEmbedId(url) {
    if (!url) return;

    /* eslint-disable-next-line no-useless-escape */
    const vimeoRegex = new RegExp(/(?:http|https)?:?\/?\/?(?:www\.)?(?:player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/, 'i');
    return url.match(vimeoRegex)[1];
  }
}

registerModule('VideoGallery', VideoGallery);
